import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CalendarClock,
  Check,
  Layers,
  RefreshCw,
  RotateCcw,
  Users,
  X,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";

import {
  applyPlatformPlanChange,
  applyPlatformSeatChange,
  changePlatformTerm,
  coerceManagementMode,
  coerceOrigin,
  formatMoney,
  getManagementState,
  isCustomerManaged,
  listCatalogPlansForOperator,
  previewPlatformPlanChange,
  previewPlatformSeatChange,
  setManagementMode,
  setPlatformAutoRenew,
  HANDBACK_BLOCKER_CODES,
  HANDBACK_NO_PAYMENT_METHOD_CODE,
  type OperatorCatalogPlanView,
  type PlatformBillingRowView,
  type PlatformManagementStateView,
  type PlatformQuoteView,
} from "../../../api/General/PlatformBilling";
import { formatDate } from "./helpers";
import { fieldClass, ErrorNote, DialogIcon, ReasonField, firstError, type BaseProps } from "./PlatformBillingDialogs";

/*
 * OPERATOR-INITIATED SUBSCRIPTION CHANGES — plan, seats, term, auto-renew, and the management
 * handover itself.
 *
 * These are the levers that make "some subscriptions are managed only by the operator" workable. The
 * customer's own portal now REFUSES these five actions on an operator-managed subscription; if the
 * operator had no equivalent, taking a subscription over would mean nobody could change it at all.
 *
 * Four rules hold across every dialog here, and they are not decoration:
 *
 *   1. BEFORE → AFTER IS ALWAYS SHOWN, in the same visual grammar, before the submit is reachable.
 *      An operator acting on a support call is reading someone else's subscription; "seats: 40 → 55"
 *      is the only thing that catches a mistyped number.
 *   2. A REASON IS MANDATORY. It is written to the subscription audit log and rendered back in the
 *      history sheet. An unexplained change to someone's bill is indistinguishable from a mistake.
 *   3. NO DIALOG SENDS AN AMOUNT. Money is quoted by the server and only displayed here. Direction
 *      comes from the quote's `isCredit` flag and never from comparing rates — on the multi-year
 *      ladder the per-seat rate falls while the term price rises, so a rate comparison is inverted.
 *   4. A PRECONDITION IS DISCLOSED, NOT ENFORCED ON SUBMIT. The handback dialog reads the server's
 *      blocker list on open and writes it out in plain sentences.
 */

/* ───────────────────────── shared presentation ───────────────────────── */

/** The one before/after grammar used by every dialog in this file. */
function BeforeAfter({
  label,
  before,
  after,
  changed = true,
}: {
  label: string;
  before: React.ReactNode;
  after: React.ReactNode;
  changed?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="flex items-baseline gap-2 text-sm tabular-nums">
        <span className={changed ? "text-muted-foreground line-through" : "text-muted-foreground"}>
          {before}
        </span>
        <ArrowRight size={12} aria-hidden="true" className="text-muted-foreground rtl:rotate-180" />
        <span className="font-semibold text-foreground">{after}</span>
      </span>
    </div>
  );
}

function ChangeSummary({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2 divide-y divide-border/60">
      {children}
    </div>
  );
}

/**
 * The money consequence of a change, in the operator's words.
 *
 * ⚠️ `isCredit` is the ONLY direction signal. `totalGross` is an unsigned magnitude.
 */
function QuotePanel({
  quote,
  loading,
  currency,
}: {
  quote: PlatformQuoteView | null;
  loading: boolean;
  currency: string;
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
        <Spinner size="sm" />
        {t("Pricing this change…")}
      </div>
    );
  }
  if (!quote) return null;

  if (quote.blockReasonCode) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        {t("The billing engine will not price this change.")}{" "}
        <span className="font-mono text-xs">{quote.blockReasonCode}</span>
      </div>
    );
  }

  const gross = formatMoney(quote.totalGross, quote.currency || currency);
  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 space-y-1">
      <p className="text-sm text-foreground">
        {quote.totalGross === 0
          ? t("Nothing is charged or credited for this change.")
          : quote.isCredit
            ? t("The customer will be credited {{amount}}.", { amount: gross })
            : t("The customer will be charged {{amount}}.", { amount: gross })}
      </p>
      <p className="text-xs text-muted-foreground">
        {t("{{seats}} seats · {{term}} months · {{rate}} per seat per month", {
          seats: quote.seats,
          term: quote.termMonths,
          rate: formatMoney(quote.ratePerSeatPerMonth, quote.currency || currency),
        })}
      </p>
      {quote.creditApplied > 0 && (
        <p className="text-xs text-muted-foreground">
          {t("Account credit applied first — {{amount}}.", {
            amount: formatMoney(quote.creditApplied, quote.currency || currency),
          })}
        </p>
      )}
    </div>
  );
}

/**
 * Shown at the top of every change dialog when the subscription is the CUSTOMER'S to manage.
 * Changing it from here is legitimate, but it overrides a choice the customer made themselves, and
 * that is worth one sentence rather than a silent equivalence between the two surfaces.
 */
function OverrideNotice({ row }: { row: PlatformBillingRowView }) {
  const { t } = useTranslation();
  if (!isCustomerManaged(row)) return null;
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
      {t(
        "This customer manages their own subscription. Changing it here overrides a choice they made, and it will appear in their billing history.",
      )}
    </div>
  );
}

/** Debounce a value so the preview call does not fire on every keystroke. */
function useDebounced<T>(value: T, delay = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

/* ═══════════════════════ 1. Change plan / term rate ═══════════════════════ */

export function PlatformPlanChangeDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<OperatorCatalogPlanView[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [planCode, setPlanCode] = useState("");
  const [termMonths, setTermMonths] = useState<number | "">("");
  const [applyImmediately, setApplyImmediately] = useState(true);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [quote, setQuote] = useState<PlatformQuoteView | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const currency = row?.currency || "AED";

  useEffect(() => {
    if (!open || !row) return;
    setPlanCode(row.planCode ?? "");
    setTermMonths("");
    setApplyImmediately(true);
    setReason("");
    setTouched(false);
    setSubmitting(false);
    setError(null);
    setQuote(null);
    setPlansError(null);

    let cancelled = false;
    setPlansLoading(true);
    listCatalogPlansForOperator(currency)
      .then((res) => {
        if (cancelled) return;
        const message = firstError(res?.errors);
        if (message) {
          setPlansError(message);
          setPlans([]);
          return;
        }
        setPlans(res.payload ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          // A catalogue that will not load must SAY SO. An empty select reads as "there is nowhere to
          // move this customer", which is a different and wrong fact.
          setPlansError(t("The plan catalogue could not be loaded. Nothing has been changed."));
          setPlans([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, row, currency, t]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.code === planCode) ?? null,
    [plans, planCode],
  );

  const termOptions = useMemo(() => {
    const months = (selectedPlan?.prices ?? [])
      .filter((p) => !p.currency || p.currency === currency)
      .map((p) => p.termMonths);
    return Array.from(new Set(months)).sort((a, b) => a - b);
  }, [selectedPlan, currency]);

  const debouncedPlan = useDebounced(planCode);
  const debouncedTerm = useDebounced(termMonths);
  const debouncedImmediate = useDebounced(applyImmediately);

  const changed =
    !!debouncedPlan &&
    (debouncedPlan !== (row?.planCode ?? "") || (debouncedTerm !== "" && debouncedTerm > 0));

  useEffect(() => {
    if (!open || !row || !changed) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    previewPlatformPlanChange(row.companyId, {
      targetPlanCode: debouncedPlan,
      targetTermMonths: debouncedTerm === "" ? null : Number(debouncedTerm),
      applyImmediately: debouncedImmediate,
      // The preview is a read. The server still requires the field, so send a marker rather than an
      // empty string — a blank reason on a read would be indistinguishable from a forgotten one.
      reason: "preview",
    })
      .then((res) => {
        if (cancelled) return;
        setQuote(firstError(res?.errors) ? null : (res.payload ?? null));
      })
      .catch(() => {
        if (!cancelled) setQuote(null);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, row, changed, debouncedPlan, debouncedTerm, debouncedImmediate]);

  if (!row) return null;

  const reasonInvalid = reason.trim().length === 0;
  const blocked = !!quote?.blockReasonCode;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid || !changed || blocked) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await applyPlatformPlanChange(row.companyId, {
        targetPlanCode: planCode,
        targetTermMonths: termMonths === "" ? null : Number(termMonths),
        applyImmediately,
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not change the plan. Nothing has been changed."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <Layers size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{t("Change plan")}</DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <OverrideNotice row={row} />
          {plansError && <ErrorNote message={plansError} />}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="pb-plan-code">
                {t("Plan")} <span className="text-destructive">*</span>
              </label>
              <select
                id="pb-plan-code"
                value={planCode}
                onChange={(e) => setPlanCode(e.target.value)}
                disabled={plansLoading || plans.length === 0}
                className={fieldClass(false)}
              >
                <option value="">{plansLoading ? t("Loading plans…") : t("Select a plan")}</option>
                {plans.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="pb-plan-term">
                {t("Term")}
              </label>
              <select
                id="pb-plan-term"
                value={termMonths === "" ? "" : String(termMonths)}
                onChange={(e) => setTermMonths(e.target.value === "" ? "" : Number(e.target.value))}
                disabled={termOptions.length === 0}
                className={fieldClass(false)}
              >
                <option value="">{t("Keep the current term")}</option>
                {termOptions.map((m) => (
                  <option key={m} value={m}>
                    {t("{{months}} months", { months: m })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">{t("When it takes effect")}</legend>
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="pb-plan-when"
                checked={applyImmediately}
                onChange={() => setApplyImmediately(true)}
                className="mt-1"
              />
              <span>
                {t("Immediately")}
                <span className="block text-xs text-muted-foreground">
                  {t("A downgrade applied now produces account credit, never a cash refund.")}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="pb-plan-when"
                checked={!applyImmediately}
                onChange={() => setApplyImmediately(false)}
                className="mt-1"
              />
              <span>
                {t("At the end of the current term")}
                <span className="block text-xs text-muted-foreground">
                  {formatDate(row.termEndUtc)}
                </span>
              </span>
            </label>
          </fieldset>

          {changed && (
            <ChangeSummary>
              <BeforeAfter
                label={t("Plan")}
                before={row.planCode ?? "—"}
                after={selectedPlan?.name ?? planCode}
                changed={planCode !== (row.planCode ?? "")}
              />
              {termMonths !== "" && (
                <BeforeAfter
                  label={t("Term")}
                  before={t("current")}
                  after={t("{{months}} months", { months: Number(termMonths) })}
                />
              )}
            </ChangeSummary>
          )}

          <QuotePanel quote={quote} loading={quoteLoading} currency={currency} />

          <ReasonField
            id="pb-plan-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. agreed on the renewal call, moving them to the 3-year rate")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} disabled={!changed || blocked}>
            {t("Apply plan change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 2. Change committed seats ═══════════════════════ */

export function PlatformSeatChangeDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<PlatformQuoteView | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const committed = row?.committedSeats ?? 0;

  useEffect(() => {
    if (!open || !row) return;
    setTarget(String(row.committedSeats ?? ""));
    setReason("");
    setTouched(false);
    setSubmitting(false);
    setError(null);
    setQuote(null);
  }, [open, row]);

  const parsed = Number(target);
  const targetInvalid = target === "" || Number.isNaN(parsed) || parsed < 0 || !Number.isInteger(parsed);
  const delta = targetInvalid ? 0 : parsed - committed;
  const debouncedDelta = useDebounced(delta);

  useEffect(() => {
    if (!open || !row || debouncedDelta === 0) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    previewPlatformSeatChange(row.companyId, { seatDelta: debouncedDelta, reason: "preview" })
      .then((res) => {
        if (cancelled) return;
        setQuote(firstError(res?.errors) ? null : (res.payload ?? null));
      })
      .catch(() => {
        if (!cancelled) setQuote(null);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, row, debouncedDelta]);

  if (!row) return null;

  const reasonInvalid = reason.trim().length === 0;
  const blocked = !!quote?.blockReasonCode;
  const currency = row.currency || "AED";

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid || targetInvalid || delta === 0 || blocked) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await applyPlatformSeatChange(row.companyId, {
        seatDelta: delta,
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not change the seat count. Nothing has been changed."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <Users size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{t("Change committed seats")}</DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <OverrideNotice row={row} />

          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {t(
              "Committed seats are what the customer pays for. Billable seats are what the daily snapshot measured. They are allowed to differ — this changes only the first.",
            )}
          </p>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="pb-seats-target">
              {t("Committed seats")} <span className="text-destructive">*</span>
            </label>
            <input
              id="pb-seats-target"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onBlur={() => setTouched(true)}
              className={`${fieldClass(touched && targetInvalid)} tabular-nums`}
            />
            <p className="text-xs text-muted-foreground">
              {t("Billable seats measured on {{date}} — {{seats}}.", {
                date: formatDate(row.billableSeatsAsOfUtc),
                seats: row.billableSeats ?? 0,
              })}
            </p>
          </div>

          {delta !== 0 && !targetInvalid && (
            <ChangeSummary>
              <BeforeAfter label={t("Committed seats")} before={committed} after={parsed} />
              <BeforeAfter
                label={t("Change")}
                before={delta > 0 ? t("adding") : t("removing")}
                after={Math.abs(delta)}
                changed={false}
              />
            </ChangeSummary>
          )}

          <QuotePanel quote={quote} loading={quoteLoading} currency={currency} />

          <ReasonField
            id="pb-seats-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. signed order form for 15 more seats, invoiced by transfer")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} disabled={delta === 0 || targetInvalid || blocked}>
            {delta > 0
              ? t("Add {{seats}} seats", { seats: delta })
              : delta < 0
                ? t("Remove {{seats}} seats", { seats: Math.abs(delta) })
                : t("Apply seat change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 3. Change the term ═══════════════════════ */

export function PlatformTermChangeDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"length" | "end">("end");
  const [months, setMonths] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !row) return;
    setMode("end");
    setMonths("");
    setEndDate(row.termEndUtc ? row.termEndUtc.slice(0, 10) : "");
    setReason("");
    setTouched(false);
    setSubmitting(false);
    setError(null);
  }, [open, row]);

  if (!row) return null;

  const monthsParsed = Number(months);
  const monthsInvalid =
    mode === "length" && (months === "" || Number.isNaN(monthsParsed) || monthsParsed < 1);
  const endInvalid = mode === "end" && !endDate;
  const unchanged =
    mode === "end" && !!row.termEndUtc && endDate === row.termEndUtc.slice(0, 10);
  const reasonInvalid = reason.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid || monthsInvalid || endInvalid || unchanged) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await changePlatformTerm(row.companyId, {
        // Mutually exclusive by construction — the server refuses a body carrying both.
        termMonths: mode === "length" ? monthsParsed : null,
        termEndUtc: mode === "end" ? new Date(`${endDate}T23:59:59Z`).toISOString() : null,
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not change the term. Nothing has been changed."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <CalendarClock size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{t("Change the term")}</DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <OverrideNotice row={row} />

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">{t("What to change")}</legend>
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="pb-term-mode"
                checked={mode === "end"}
                onChange={() => setMode("end")}
                className="mt-1"
              />
              <span>
                {t("Move the term end date")}
                <span className="block text-xs text-muted-foreground">
                  {t("The committed rate is untouched. Use this to extend goodwill time.")}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="pb-term-mode"
                checked={mode === "length"}
                onChange={() => setMode("length")}
                className="mt-1"
              />
              <span>
                {t("Set a new committed term length")}
                <span className="block text-xs text-muted-foreground">
                  {t("Re-rates the subscription against the ladder at the next renewal.")}
                </span>
              </span>
            </label>
          </fieldset>

          {mode === "end" ? (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="pb-term-end">
                {t("Term ends")} <span className="text-destructive">*</span>
              </label>
              <input
                id="pb-term-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onBlur={() => setTouched(true)}
                className={fieldClass(touched && endInvalid)}
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="pb-term-months">
                {t("Committed term in months")} <span className="text-destructive">*</span>
              </label>
              <input
                id="pb-term-months"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                onBlur={() => setTouched(true)}
                className={`${fieldClass(touched && monthsInvalid)} tabular-nums`}
              />
            </div>
          )}

          {!unchanged && !monthsInvalid && !endInvalid && (
            <ChangeSummary>
              {mode === "end" ? (
                <BeforeAfter
                  label={t("Term ends")}
                  before={formatDate(row.termEndUtc)}
                  after={formatDate(endDate ? `${endDate}T00:00:00Z` : null)}
                />
              ) : (
                <BeforeAfter
                  label={t("Committed term")}
                  before={t("current")}
                  after={t("{{months}} months", { months: monthsParsed })}
                />
              )}
            </ChangeSummary>
          )}

          <ReasonField
            id="pb-term-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. two weeks added while the bank transfer clears")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} disabled={unchanged}>
            {t("Apply term change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 4. Auto-renew ═══════════════════════ */

export function PlatformAutoRenewDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const turningOff = row.autoRenew;
  const reasonInvalid = reason.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await setPlatformAutoRenew(row.companyId, {
        autoRenew: !row.autoRenew,
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not change auto-renew. Nothing has been changed."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone={turningOff ? "amber" : "neutral"}>
              <RefreshCw size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{turningOff ? t("Turn off auto-renew") : t("Turn on auto-renew")}</DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <OverrideNotice row={row} />

          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {turningOff
              ? t(
                  "The subscription runs to the end of its term and then stops renewing. Nothing is cancelled today and no refund is calculated.",
                )
              : t(
                  "The subscription will renew at the end of its term. A card on file is still required before anything can actually be charged.",
                )}
          </p>

          <ChangeSummary>
            <BeforeAfter
              label={t("Auto-renew")}
              before={row.autoRenew ? t("On") : t("Off")}
              after={row.autoRenew ? t("Off") : t("On")}
            />
            <BeforeAfter
              label={t("Term ends")}
              before={formatDate(row.termEndUtc)}
              after={formatDate(row.termEndUtc)}
              changed={false}
            />
          </ChangeSummary>

          <ReasonField
            id="pb-autorenew-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. customer confirmed they are not renewing after this term")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button
            variant={turningOff ? "destructive" : "default"}
            onClick={submit}
            loading={submitting}
          >
            {turningOff ? t("Stop renewing at term end") : t("Renew at term end")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 5. Who manages this subscription ═══════════════════════ */

/**
 * TAKE OVER / HAND BACK.
 *
 * The handback direction is the one with preconditions, and they are READ ON OPEN and written out as
 * sentences. Handing a subscription back to a customer who has no card on file does not fail loudly —
 * it silently lapses at the next renewal, which is the worst possible outcome and exactly what the
 * server's blocker list exists to prevent. So the dialog shows the checklist BEFORE the operator
 * invests in typing a reason, and the submit is disabled while a blocker stands.
 *
 * 🔴 CO-37 — WITH ONE EXCEPTION, AND IT IS NOT A LOOSENING. "No card on file" was an ABSOLUTE
 * blocker whose own remedy was impossible: the customer cannot add a card while the subscription is
 * operator-managed, so card-then-handback could never happen and handback-then-card was refused. The
 * subscription was stuck in a state neither party could move. The correct order is handback first,
 * card second — so that one blocker becomes an explicit, audited acknowledgement instead of a wall.
 * Every other blocker (outstanding balance, negotiated rate, no provider customer) still stands.
 */
export function PlatformManagementModeDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<PlatformManagementStateView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CO-37 — the operator's explicit "yes, hand it back with no card". `needsAck` also latches on if
  // the SERVER is the one that raises it, so a stale/absent blocker list cannot hide the escape.
  const [ackNoCard, setAckNoCard] = useState(false);
  const [needsAck, setNeedsAck] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async (companyId: number) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getManagementState(companyId);
      if (!mounted.current) return;
      const message = firstError(res?.errors);
      if (message) {
        setLoadError(message);
        setState(null);
        return;
      }
      setState(res.payload ?? null);
    } catch {
      if (mounted.current) {
        setLoadError(null);
        setState(null);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !row) return;
    setReason("");
    setTouched(false);
    setSubmitting(false);
    setError(null);
    setState(null);
    setAckNoCard(false);
    setNeedsAck(false);
    void load(row.companyId);
  }, [open, row, load]);

  if (!row) return null;

  // Fail-closed, exactly as the server resolves it. `state` refines it once it arrives.
  const customerManaged = state ? isCustomerManaged(state) : isCustomerManaged(row);
  const handingBack = !customerManaged;

  const origin = coerceOrigin(state?.origin ?? row.origin);
  const mode = coerceManagementMode(state?.managementMode ?? row.managementMode);

  const blockerText = (code: string, detail?: string | null): string => {
    switch (code) {
      case HANDBACK_BLOCKER_CODES.NoPaymentMethod:
        // CO-37 — no longer "the customer must add a card first": they CANNOT while we manage it.
        return t(
          "There is no payment card on file. The customer can only add one once this is self-serve again, so confirm below to hand it back and let them add one.",
        );
      case HANDBACK_BLOCKER_CODES.NoProviderCustomer:
        return t(
          "This company has no account with the payment provider. Converting it to manual invoicing removed the link, and a fresh checkout is needed to restore it.",
        );
      case HANDBACK_BLOCKER_CODES.OutstandingBalance:
        return t("There is money still outstanding. Settle or write it off before handing back.");
      case HANDBACK_BLOCKER_CODES.NegotiatedRate:
        return t(
          "This subscription is on a negotiated rate that is not in the plan catalogue. Self-serve billing can only charge catalogue prices.",
        );
      case HANDBACK_BLOCKER_CODES.BillingDisabled:
        return t("Self-serve billing is switched off on this environment.");
      case HANDBACK_BLOCKER_CODES.NoSubscription:
        return t("This company has no billing subscription record to hand back.");
      default:
        // An unrecognised blocker must still be readable rather than silently dropped.
        return detail || code;
    }
  };

  /*
   * The two flags the acknowledgement sentence turns on. Both resolve `state ?? row` for the same
   * reason `mode` and `origin` do: the management-state read can fail, and a sentence the operator
   * makes an irreversible decision from must not silently fall back to the WRONG outcome.
   *
   * `neverExpires` is only on `row` today — see the DTO note on PlatformManagementStateView — so the
   * optional read on `state` is forward-compatible, not dead code.
   */
  const effectiveAutoRenew = state?.autoRenew ?? row.autoRenew;
  const neverExpires = (state?.neverExpires ?? row.neverExpires) === true;

  const blockers = state?.handbackBlockers ?? [];

  // CO-37 — the one blocker that can be acknowledged, separated from the ones that cannot.
  const noCardBlocker = blockers.some(
    (b) => b.code === HANDBACK_BLOCKER_CODES.NoPaymentMethod,
  );
  const hardBlockers = blockers.filter(
    (b) => b.code !== HANDBACK_BLOCKER_CODES.NoPaymentMethod,
  );
  const showAck = handingBack && (needsAck || noCardBlocker);
  const acknowledged = showAck && ackNoCard;

  const blocked =
    handingBack &&
    state !== null &&
    (hardBlockers.length > 0 ||
      // `canHandBack === false` is only overridable when the no-card blocker is the ONLY reason.
      (state.canHandBack === false && !(noCardBlocker && hardBlockers.length === 0)) ||
      (noCardBlocker && !ackNoCard));
  const reasonInvalid = reason.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid || blocked || loading) return;
    // A server-raised acknowledgement must be ticked before the retry is sent.
    if (needsAck && !ackNoCard) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await setManagementMode(row.companyId, {
        mode: handingBack ? 0 : 1,
        reason: reason.trim(),
        acknowledgeNoPaymentMethod: acknowledged || undefined,
      });
      const message = firstError(result?.errors);
      if (message) {
        // CO-37 — the server refused for want of a card. Reveal the acknowledgement rather than
        // showing a dead end: there is no other action the operator could take to satisfy it.
        if (result?.errors?.[0]?.code === HANDBACK_NO_PAYMENT_METHOD_CODE) setNeedsAck(true);
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not change who manages this subscription. Nothing has been changed."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone={handingBack ? "neutral" : "amber"}>
              <RotateCcw size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>
                {handingBack
                  ? t("Hand back to the customer")
                  : t("Take over this subscription")}
              </DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {handingBack
              ? t(
                  "The company's HR and Admin users get their plan, seat, renewal and cancellation controls back. Everything stays visible to them either way — this only changes who may press the buttons.",
                )
              : t(
                  "The company's HR and Admin users keep full read access — plan, seats, invoices, credit notes, downloads — but plan, seat, renewal and cancellation changes will come to us instead. They get a Request a change route in their portal.",
                )}
          </p>

          {loading ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
              <Spinner size="sm" />
              {t("Checking this subscription…")}
            </div>
          ) : (
            <>
              <ChangeSummary>
                <BeforeAfter
                  label={t("Managed by")}
                  before={mode === "OperatorManaged" ? t("Mowazaf") : t("The customer")}
                  after={handingBack ? t("The customer") : t("Mowazaf")}
                />
                {origin && (
                  <BeforeAfter
                    label={t("Originally started by")}
                    before={
                      origin === "SelfServeCheckout"
                        ? t("The customer, through checkout")
                        : origin === "OperatorProvisioned"
                          ? t("Mowazaf, on their behalf")
                          : origin === "Trial"
                            ? t("A trial")
                            : origin === "Migrated"
                              ? t("Migrated from before self-serve billing")
                              : t("Not recorded")
                    }
                    after={t("unchanged")}
                    changed={false}
                  />
                )}
              </ChangeSummary>

              {/* The preconditions, disclosed rather than enforced on submit. */}
              {handingBack && state !== null && (
                <div className="rounded-md border border-border bg-card p-3 space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("Before this can go back to self-serve")}
                  </h3>
                  {blockers.length === 0 ? (
                    <p className="flex items-start gap-2 text-sm text-foreground">
                      <Check
                        size={15}
                        aria-hidden="true"
                        className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400"
                      />
                      {t("Everything the billing engine needs is in place.")}
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {blockers.map((b, i) => (
                        <li key={`${b.code}-${i}`} className="flex items-start gap-2 text-sm text-foreground">
                          <X
                            size={15}
                            aria-hidden="true"
                            className="mt-0.5 shrink-0 text-destructive"
                          />
                          <span>{blockerText(b.code, b.detail)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {loadError && <ErrorNote message={loadError} />}
            </>
          )}

          {/* 🔴 CO-37 — the deadlock escape, stated in full rather than as a bare checkbox. */}
          {showAck && (
            <div className="rounded-md border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
              <p className="text-sm text-amber-900 dark:text-amber-200">
                {t(
                  "The customer cannot add a card while we manage this subscription, so there is no order in which they could add one first. Hand it back and they can add one immediately.",
                )}
              </p>
              <label className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200 select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/20"
                  checked={ackNoCard}
                  onChange={(e) => setAckNoCard(e.target.checked)}
                />
                <span>
                  {/*
                    CO-37 repair — the promise must match what will actually happen to THIS row.
                    With auto-renew off nothing ever renews, so nothing ever fails and nothing is
                    ever dunned; the server instead moves the term to non-renewing so access
                    resolves at term end. Saying "dunning will chase them" there was the false
                    reassurance that hid a permanent free tenant.

                    🔴 THREE OUTCOMES, NOT TWO — and the branch must mirror
                    PlatformBillingService.Management.cs exactly, because the operator is making an
                    irreversible decision from this sentence.

                      1. NeverExpires  → nothing expires at all (CO-23). The row keeps FULL access with
                         no card until an Operator ends the grant. Any sentence promising the access
                         will end is FALSE here, and the server's own handback normalisation excludes
                         NeverExpires outright, so this case is checked FIRST.
                      2. auto-renew OFF → normalised to NonRenewing; access degrades at term end.
                      3. auto-renew ON  → a renewal is attempted, it fails with no card, dunning runs.

                    ⚠️ `state` is the refinement, `row` is the fallback — the state read can fail (and
                    the sentence must still be right), so both flags resolve `state ?? row`. `row`
                    carries `neverExpires`; the management-state payload does not yet (see the DTO note
                    on PlatformManagementStateView).

                    ⚠️ "unless the customer adds a card" was REMOVED, not reworded: adding or defaulting
                    a payment method does NOT re-arm AutoRenew and does NOT flip NonRenewing back to
                    Active. Only SetAutoRenewAsync(true) or a completed checkout do.
                  */}
                  {neverExpires
                    ? t(
                        "Hand back with no payment method on file. This company carries a perpetual (never-expires) grant, so nothing lapses and nothing is dunned — it keeps full access with no card until an Operator ends the grant.",
                      )
                    : !effectiveAutoRenew
                      ? t(
                          "Hand back with no payment method on file. Auto-renew is off, so the term will be set to non-renewing and access drops to Restricted at term end. Adding a card does not change this — the customer must turn auto-renew back on or complete a new checkout.",
                        )
                      : t(
                          "Hand back with no payment method on file. If the next renewal has no card, normal dunning will chase the customer.",
                        )}
                </span>
              </label>
            </div>
          )}

          <ReasonField
            id="pb-management-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={
              handingBack
                ? t("e.g. contract ended, customer is back on the standard plan with a card on file")
                : t("e.g. signed enterprise agreement, we invoice them by bank transfer")
            }
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} disabled={blocked || loading}>
            {handingBack ? t("Hand back to self-serve") : t("Take over management")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
